/**
 * Builds the report archive and pushes it to our bucket.
 *
 * Every piece of the report is optional and independently guarded: a report that
 * only manages to carry the player's description is still worth having, so no
 * single failed collector is allowed to sink the submission.
 */

import { zipSync } from 'fflate';
import { getVersionString } from '../../../shared/version';
import { WebServicesEndpointUrl, ServicesFetchTimeout } from '../../../shared/constants';
import { getCapturedLogText, recordInternal } from './consoleCapture';
import { collectSystemInfo } from './systemInfo';
import { requestServerWorkerLogs, hasServerWorker } from './workerLogBridge';
import { collectGameSaves } from './gameSaves';
import { generateReportId } from './reportId';

export type ReportOptions = {
	description: string;
	includeSystemInfo: boolean;
	/**
	 * Taken when the player pressed the report button, before the modal was on
	 * screen. Null covers every reason there isn't one - the player declined it,
	 * the capture failed, or this build can't take them - and the UI only offers
	 * the choice when it is actually holding an image, so nothing else is needed
	 * to know whether to send one.
	 */
	screenshot: Uint8Array | null;
};

export type SubmitResult =
	| { ok: true; reportId: string; key: string }
	| { ok: false; message: string };

type ContextProvider = () => Record<string, any>;

let contextProvider: ContextProvider | null = null;

/** Lets the game attach whatever state it thinks is worth knowing. */
export function registerContextProvider(fn: ContextProvider) {
	contextProvider = fn;
}

const textEncoder = new TextEncoder();

function encode(text: string) {
	return textEncoder.encode(text);
}

/**
 * Zips synchronously on purpose.
 *
 * fflate's async path spawns a worker from a blob URL, which the desktop build
 * can't do from its file:// origin. A report is a few hundred KB of text plus a
 * screenshot, so compressing it in one go costs a brief hitch on a screen the
 * player is already stopped on.
 */
function zipFiles(files: Record<string, Uint8Array>): Uint8Array {
	return zipSync(files, { level: 6 });
}

/** Runs a collector, swallowing anything it throws into the report itself. */
async function tryCollect<T>(name: string, fn: () => Promise<T> | T, failures: Record<string, string>): Promise<T | null> {
	try {
		return await fn();
	} catch (e: any) {
		const message = String(e?.stack ?? e?.message ?? e);
		failures[name] = message;
		recordInternal(`bug report collector "${name}" failed: ${message}`);
		return null;
	}
}

async function getUploadEndpoint(): Promise<string> {
	const abort = new AbortController();
	const timer = setTimeout(() => abort.abort(), ServicesFetchTimeout);
	try {
		const response = await fetch(WebServicesEndpointUrl, { signal: abort.signal });
		if (!response.ok) throw new Error(`services list returned ${response.status}`);
		const services = await response.json();
		const url = services?.UploadBugReportURL;
		if (typeof url !== 'string' || url.length === 0) {
			throw new Error('services list has no UploadBugReportURL');
		}
		return url;
	} finally {
		clearTimeout(timer);
	}
}

async function buildArchive(options: ReportOptions, reportId: string): Promise<Uint8Array> {
	const failures: Record<string, string> = {};
	const files: Record<string, Uint8Array> = {};

	const context = await tryCollect('context', () => contextProvider?.() ?? null, failures);
	const systemInfo = options.includeSystemInfo
		? await tryCollect('systemInfo', () => collectSystemInfo(), failures)
		: null;

	/** Collects one block of text into its own file, or records why it couldn't. */
	const addTextFile = async (name: string, fileName: string, collect: () => Promise<string | null> | string | null) => {
		const text = await tryCollect(name, collect, failures);
		if (typeof text === 'string' && text.length > 0) files[fileName] = encode(text);
	};

	await addTextFile('browserConsole', 'browser-console.log', getCapturedLogText);
	await addTextFile('serverWorker', 'server-worker.log', requestServerWorkerLogs);
	await addTextFile('electronMain', 'electron-main.log', () => {
		const api = (window as any).electronApi?.bugReport;
		return api?.getMainProcessLogs ? api.getMainProcessLogs() : null;
	});

	// Already taken, back when the player pressed the report button.
	if (options.screenshot) files['screenshot.png'] = options.screenshot;

	// The autosaves let us reload the game into the state the bug happened in.
	const saves = await tryCollect('gameSaves', () => collectGameSaves(), failures);
	if (saves) {
		for (const save of saves.saves) {
			files[save.fileName] = encode(save.contents);
		}
		for (const [name, reason] of Object.entries(saves.skipped)) {
			failures[`gameSave:${name}`] = reason;
		}
	}

	if (options.description.trim().length > 0) {
		files['description.txt'] = encode(options.description);
	}

	const report = {
		schema: 1,
		reportId,
		submittedAt: new Date().toISOString(),
		version: getVersionString(),
		description: options.description,
		shared: {
			screenshot: !!options.screenshot,
			systemInfo: options.includeSystemInfo,
		},
		contents: Object.keys(files),
		context,
		systemInfo,
		// Present only when something went wrong gathering the rest - being able
		// to tell "no server logs" from "server logs blew up" matters.
		collectionFailures: Object.keys(failures).length > 0 ? failures : undefined,
		serverWorkerAvailable: hasServerWorker(),
	};
	files['report.json'] = encode(JSON.stringify(report, null, 2));

	return zipFiles(files);
}

export async function submitBugReport(options: ReportOptions): Promise<SubmitResult> {
	// Minted here rather than server side so the id we show the player is the
	// same one recorded inside the archive and used as its name in the bucket.
	const reportId = generateReportId();

	let archive: Uint8Array;
	try {
		archive = await buildArchive(options, reportId);
	} catch (e: any) {
		return { ok: false, message: `Could not package the report: ${e?.message ?? e}` };
	}

	let endpoint: string;
	try {
		endpoint = await getUploadEndpoint();
	} catch (e: any) {
		return { ok: false, message: 'Could not reach the reporting service. Check your connection and try again.' };
	}

	let uploadUrl: string;
	let key: string;
	try {
		const response = await fetch(endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ size: archive.byteLength, version: getVersionString(), reportId }),
		});

		if (response.status === 429) {
			return { ok: false, message: 'Too many reports have been sent from this connection. Please try again later.' };
		}
		if (response.status === 413) {
			// Only suggest dropping the screenshot when there is one to drop.
			return {
				ok: false,
				message: options.screenshot
					? 'This report is too large to send. Try again without the screenshot.'
					: 'This report is too large to send.',
			};
		}
		if (!response.ok) {
			return { ok: false, message: `The reporting service refused the report (error ${response.status}).` };
		}

		const json = await response.json();
		uploadUrl = json?.uploadUrl;
		key = json?.key;
		if (!uploadUrl) throw new Error('no upload url in response');
	} catch (e: any) {
		return { ok: false, message: 'Could not reach the reporting service. Check your connection and try again.' };
	}

	try {
		// The array goes straight out as the body. Wrapping it in a Blob would
		// hand the bytes to the browser's blob store, which is free to page large
		// ones to disk, and would need a full copy first because fflate can return
		// a view into a larger buffer. fetch honours the view's own bounds, so
		// this both stays in memory and avoids doubling peak usage.
		const response = await fetch(uploadUrl, {
			// The cast is for newer TypeScript, which types a Uint8Array as
			// possibly SharedArrayBuffer-backed and so not a valid body. fflate
			// never returns one of those.
			method: 'PUT',
			body: archive as unknown as BodyInit,
			headers: {
				'Content-Type': 'application/zip',
				// Signed into the URL: S3 refuses the write if something is already
				// stored under this id, so no report can ever overwrite another.
				'If-None-Match': '*',
			},
		});
		if (response.status === 412) {
			return { ok: false, message: 'That report id is already taken. Please try again.' };
		}
		if (!response.ok) {
			return { ok: false, message: `Upload failed (error ${response.status}). Please try again.` };
		}
	} catch (e: any) {
		return { ok: false, message: 'Upload failed. Check your connection and try again.' };
	}

	// Also logged so the id is still recoverable from the console if the player
	// closes the modal before writing it down.
	console.log(`Bug report submitted. Report ID: ${reportId}`);
	return { ok: true, reportId, key };
}
